import { useState, useEffect } from 'react'
import { useLanguage } from '@shared/contexts/LanguageContext'
import { isYouTubeUrl, getYouTubeThumbnail } from '@shared/utils/videoUtils'
import GuardianExpoSubPageLayout from '../components/GuardianExpoSubPageLayout'
import ChallengeScheduleSection from '../components/ChallengeScheduleSection'
import ActivityVideosSection from '../components/ActivityVideosSection'
import PhotoGridSection from '../components/PhotoGridSection'
import ExpoVideoModal from '../components/ExpoVideoModal'
import { Lightbox } from '@shared/units/Lightbox'
import { getChallengeSchedule, EXPO_EVENT, getChallengeRankList } from '../api/guardianExpoService'
import { useExpoMedia } from '../hooks/useExpoMedia'
import { GuardianExpoSection } from '../components/GuardianExpoSubPageLayout'

export default function GuardianExpoChallengePage() {
  const { t } = useLanguage()
  const [scheduleDays, setScheduleDays] = useState([])
  const [challengeRankList, setChallengeRankList] = useState([])
  const { videos, photos } = useExpoMedia(EXPO_EVENT.CHALLENGE)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [photoLightboxIndex, setPhotoLightboxIndex] = useState(null)

  useEffect(() => {
    getChallengeSchedule()
      .then(setScheduleDays)
      .catch((err) => console.error('Failed to fetch challenge schedule:', err))
    getChallengeRankList()
      .then(setChallengeRankList)
      .catch((err) => console.error('Failed to fetch challenge rank:', err))
  }, [])

  const firstPlace = challengeRankList.find((item) => item.category === '總冠軍')?.monastery_abbr ?? challengeRankList[0]?.monastery_abbr ?? ''
  const secondPlace = challengeRankList.find((item) => item.category === '亞軍')?.monastery_abbr ?? ''
  const thirdPlace = challengeRankList.find((item) => item.category === '季軍')?.monastery_abbr ?? ''
  const hasAwardWinners = challengeRankList.some((item) => item.monastery_abbr || item.monastery_name)

  return (
    <GuardianExpoSubPageLayout
      bannerSrc="/image/guardian-expo/banner_challenge_desktop.webp"
      bannerAlt={t('guardianExpo.challenge.pageTitle')}
      content={t('guardianExpo.challenge.introContent')}
    >
      {hasAwardWinners && (
        <GuardianExpoSection titleBlack={t('guardianExpo.challenge.scheduleTitleBlack')} titleRed={t('guardianExpo.challenge.awardTitleRed')} first={true}>
          <div className="relative w-full max-w-[480px] mx-auto rounded-2xl overflow-hidden">
            <img
              src="/image/guardian-expo/awards.webp"
              alt="award"
              className="w-full h-auto block"
            />
            {/* Podium 2, 1, 3 Team Names */}
            <div className="absolute inset-x-0 bottom-[2%] flex justify-between items-center pointer-events-none px-[13%]">
              <div className="w-[30%] h-[48px] flex justify-center items-center">
                <span className="font-['Noto_Sans_TC'] font-bold text-2xl md:text-xl  text-[#BC313D] tracking-wider underline underline-offset-4 decoration-[#7f7772]" style={{ textShadow: '2px 2px 5px rgba(0, 0, 0, 0.3)' }}>
                  {secondPlace}
                </span>
              </div>
              {/* <div className="relative w-[34%] flex justify-center items-center">
                <img
                  src="/image/guardian-expo/frame.png"
                  alt="1st place frame"
                  className="w-[90%] h-[30px] block"
                />
                <span className="absolute inset-0 flex items-center justify-center font-['Noto_Sans_TC'] font-bold text-2xl md:text-xl text-[#BC313D] tracking-wider pb-[8px]">
                  {firstPlace}
                </span>
              </div> */}
              <div className="w-[30%] h-[48px] flex justify-center items-center">
                <span className="font-['Noto_Sans_TC'] font-bold text-3xl md:text-xl text-[#BC313D] tracking-wider underline underline-offset-4 decoration-[#7f7772]" style={{ textShadow: '2px 2px 5px rgba(0, 0, 0, 0.3)' }}>
                  {firstPlace}
                </span>
              </div>
              <div className="w-[30%] h-[48px] flex justify-center items-center">
                <span className="font-['Noto_Sans_TC'] font-bold text-2xl md:text-xl  text-[#BC313D] tracking-wider underline underline-offset-4 decoration-[#7f7772]" style={{ textShadow: '2px 2px 5px rgba(0, 0, 0, 0.3)' }}>
                  {thirdPlace}
                </span>
              </div>
            </div>
          </div>
        </GuardianExpoSection>
      )}
      {
        scheduleDays.length > 0 && (
          <ChallengeScheduleSection
            titleBlack={t('guardianExpo.challenge.scheduleTitleBlack')}
            titleRed={t('guardianExpo.challenge.scheduleTitleRed')}
            days={scheduleDays}
            columnLabels={{
              arrival: t('guardianExpo.challenge.scheduleColumnArrival'),
              match: t('guardianExpo.challenge.scheduleColumnMatch'),
              departure: t('guardianExpo.challenge.scheduleColumnDeparture'),
            }}
            first={!hasAwardWinners}
          />
        )
      }

      {videos.length > 0 && <ActivityVideosSection
        first={!hasAwardWinners && scheduleDays.length === 0}
        videos={videos.map((video) => ({
          id: video.expo_media_id,
          thumbnail: video.media_url && isYouTubeUrl(video.media_url) ? getYouTubeThumbnail(video.media_url) : undefined,
          title: video.title,
          onClick: () => setSelectedVideo(video),
        }))}
      />}

      {photos.length > 0 && <PhotoGridSection
        titleBlack={t('guardianExpo.challenge.photosTitleBlack')}
        titleRed={t('guardianExpo.challenge.photosTitleRed')}
        photos={photos.map((photo) => ({ id: photo.expo_media_id, src: photo.media_url, alt: photo.title }))}
        onPhotoClick={(idx) => setPhotoLightboxIndex(idx)}
        last
      />}

      <ExpoVideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />

      {
        photoLightboxIndex !== null && (
          <Lightbox
            images={photos.map((photo) => photo.media_url)}
            index={photoLightboxIndex}
            onIndexChange={setPhotoLightboxIndex}
            onClose={() => setPhotoLightboxIndex(null)}
          />
        )
      }
    </GuardianExpoSubPageLayout >
  )
}
